import { DialogComponent } from '@theme/dialog';
import { buildShareCartUrl, findUnserialisableLines, isTesoLine } from '@theme/share-cart-url';

/**
 * TESO checkout confirmation flow.
 *
 * The cart CTA (`[data-teso-checkout-open]` rendered by
 * snippets/cart-summary.liquid when the cart's external checkout type is
 * `teso`, detected via `properties[_checkout_type]`) opens this dialog.
 * On confirm, the flow is strictly ordered:
 *
 *   1. Snapshot the cart via /cart.js.
 *   2. Build the Bunnings share-cart URL from the TESO lines only.
 *   3. Await POST /cart/clear.js — the modal copy promises the cart clears.
 *   4. Redirect to Bunnings.
 *
 * If any TESO line lacks a usable SKU, the URL cannot be built, or the clear
 * fails, the redirect is aborted and the cart is left intact. TESO items must
 * never reach Shopify checkout. See docs/business-rules/teso-promotion.md.
 *
 * @typedef {object} TesoCheckoutRefs
 * @property {HTMLDialogElement} dialog
 * @property {HTMLButtonElement} confirmButton
 * @property {HTMLButtonElement} cancelButton
 * @property {HTMLButtonElement} closeButton
 * @property {HTMLElement} errorMessage
 *
 * @extends {DialogComponent<TesoCheckoutRefs>}
 */
class TesoCheckoutDialog extends DialogComponent {
  /** @type {boolean} */
  #redirecting = false;

  open() {
    this.#setError('');
    this.#setBusy(false);
    this.showDialog();
  }

  handleCancel = () => {
    if (this.#redirecting) return;
    this.closeDialog();
  };

  handleConfirm = async () => {
    if (this.#redirecting) return;
    this.#redirecting = true;
    this.#setBusy(true);
    this.#setError('');

    let missingSku = false;

    try {
      // 1. Snapshot the cart.
      const cartResponse = await fetch('/cart.js', { credentials: 'same-origin' });
      if (!cartResponse.ok) throw new Error(`Failed to fetch cart: ${cartResponse.status}`);
      const cart = await cartResponse.json();

      const tesoItems = (cart.items || []).filter(isTesoLine);
      if (tesoItems.length === 0) throw new Error('No TESO lines in cart');

      // 2. Every TESO line must survive the handoff. buildShareCartUrl skips
      // lines it cannot serialise, so without this check a cart mixing
      // SKU-bearing and SKU-less lines would still yield a URL, and step 3
      // would clear the cart and send Bunnings an incomplete order.
      const droppedItems = findUnserialisableLines(tesoItems);
      if (droppedItems.length > 0) {
        missingSku = true;
        throw new Error(`${droppedItems.length} TESO line(s) have no usable SKU`);
      }

      const url = buildShareCartUrl(
        { items: tesoItems },
        {
          baseUrl: this.dataset.baseUrl || '',
          utmSource: this.dataset.utmSource || '',
        }
      );
      // Reaching here with '' means the configured base URL is unparseable, not
      // a SKU problem, so this keeps the generic error message.
      if (!url) throw new Error('Share-cart URL could not be built');

      // 3. Clear the cart before leaving — never redirect if this fails.
      const clearResponse = await fetch('/cart/clear.js', {
        method: 'POST',
        credentials: 'same-origin',
      });
      if (!clearResponse.ok) throw new Error(`Failed to clear cart: ${clearResponse.status}`);

      // 4. Hand off to Bunnings.
      window.location.href = url;
    } catch (error) {
      console.error('[teso-checkout]', error);
      this.#redirecting = false;
      this.#setBusy(false);
      const message = missingSku
        ? this.dataset.errorMessageMissingSku || this.dataset.errorMessage || ''
        : this.dataset.errorMessage || '';
      this.#setError(message);
    }
  };

  /**
   * @param {boolean} busy
   */
  #setBusy(busy) {
    const { confirmButton, cancelButton, closeButton } = this.refs;
    if (confirmButton) {
      confirmButton.disabled = busy;
      confirmButton.setAttribute('aria-busy', busy ? 'true' : 'false');
    }
    if (cancelButton) cancelButton.disabled = busy;
    if (closeButton) closeButton.disabled = busy;
  }

  /**
   * @param {string} message
   */
  #setError(message) {
    const { errorMessage } = this.refs;
    if (!errorMessage) return;
    errorMessage.textContent = message;
    errorMessage.classList.toggle('hidden', !message);
  }
}

if (!customElements.get('teso-checkout-dialog')) {
  customElements.define('teso-checkout-dialog', TesoCheckoutDialog);
}

// Delegated listener: the cart drawer and cart page re-render via section
// morphing, so the CTA node is replaced frequently. Listening at the
// document level keeps the hook alive across re-renders.
document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
  const trigger = target?.closest('[data-teso-checkout-open]');
  if (!trigger || (trigger instanceof HTMLButtonElement && trigger.disabled)) return;

  event.preventDefault();
  const dialog = document.getElementById('teso-checkout-dialog');
  if (dialog instanceof TesoCheckoutDialog) {
    dialog.open();
  }
});
